import type { Fund, HoldingsResponse, Period } from '@nav/shared';

import { Card } from '@/components/ui/Card';
import { EmptyState, ErrorState, LoadingRegion, Skeleton } from '@/components/ui/states';
import { EM_DASH, formatPeriodId } from '@/lib/format';
import { BRAND, sectorColor } from '../palette';
import { sflShortName, sflTabLabel } from '../sflProducts';

/**
 * `Portfolio Holdings` — the month timeline and the ranked security list.
 *
 * Ported verbatim: the card chrome, the month pill row, the two-column
 * (Nama Efek / Sektor) grid, the zebra rows, the pill-shaped sector badge with
 * its 18-hex alpha tint, and the instrument-count footer.
 *
 * Changed:
 *   - The component's own Bancassurance/Agency selector is GONE. The page above
 *     already owns the business model and the fund; having a second, unrelated
 *     switcher here made the card contradict the rest of the screen.
 *   - Months come from `HoldingsResponse.availablePeriods` instead of
 *     `Object.keys()` on a hardcoded record.
 *   - `sector` is nullable in the contract and genuinely absent from the source
 *     workbook, so the badge renders only when a sector exists; otherwise the
 *     row gets a neutral em-dash chip. No sector is ever invented.
 */

function SectorChip({ sector }: { sector: string | null }) {
  if (!sector) {
    return (
      <span
        className="shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap"
        style={{ background: '#F1F5F9', color: '#94A3B8' }}
        title="Sektor tidak tersedia pada sumber data"
      >
        {EM_DASH}
      </span>
    );
  }
  const color = sectorColor(sector);
  return (
    <span
      className="shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap"
      style={{ background: `${color}18`, color }}
    >
      {sector}
    </span>
  );
}

function MonthTimeline({
  periods,
  selected,
  onSelect,
}: {
  periods: Period[];
  selected: Period | null;
  onSelect: (period: Period) => void;
}) {
  if (periods.length === 0) return null;
  return (
    <div
      className="flex items-center gap-1.5 overflow-x-auto pb-1 no-scrollbar"
      role="group"
      aria-label="Periode holdings"
    >
      {periods.map((period) => {
        const isActive = selected === period;
        return (
          <button
            key={period}
            type="button"
            onClick={() => onSelect(period)}
            aria-pressed={isActive}
            className="shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
            style={
              isActive
                ? { background: BRAND.red, color: '#fff', border: '1.5px solid transparent' }
                : { background: '#fff', color: BRAND.textDim, border: `1.5px solid ${BRAND.panelBorder}` }
            }
          >
            {formatPeriodId(period)}
          </button>
        );
      })}
    </div>
  );
}

export function PortfolioHoldings({
  fund,
  data,
  selectedPeriod,
  onSelectPeriod,
  isLoading,
  error,
  onRetry,
}: {
  fund: Fund | null;
  data: HoldingsResponse | undefined;
  selectedPeriod: Period | null;
  onSelectPeriod: (period: Period) => void;
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
}) {
  const periods = data?.availablePeriods ?? [];
  const period = data?.period ?? selectedPeriod;
  const holdings = data?.holdings ?? [];

  const cardHeader = (
    <div className="px-5 pt-5 pb-3" style={{ borderBottom: `1px solid ${BRAND.panelBorder}` }}>
      <p
        className="uppercase mb-0.5"
        style={{ fontSize: 10, letterSpacing: '1.5px', color: BRAND.textFaint, fontFamily: 'var(--font-sans)' }}
      >
        Portfolio Holdings · {period ? formatPeriodId(period) : EM_DASH}
      </p>
      <p className="text-sm font-semibold" style={{ fontFamily: 'var(--font-display)', color: BRAND.ink }}>
        {fund ? `${sflTabLabel(fund.code)} · ${fund.currency}` : EM_DASH}
      </p>
    </div>
  );

  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="flex gap-1.5">
          <Skeleton width={78} height={30} radius={8} />
          <Skeleton width={78} height={30} radius={8} />
          <Skeleton width={78} height={30} radius={8} />
        </div>
        <Card>
          {cardHeader}
          <LoadingRegion label="Memuat portfolio holdings" className="p-4">
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} style={{ marginBottom: 12 }}>
                <Skeleton height={16} radius={5} />
              </div>
            ))}
          </LoadingRegion>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        {cardHeader}
        <ErrorState error={error} onRetry={onRetry} compact />
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <MonthTimeline periods={periods} selected={period} onSelect={onSelectPeriod} />

      <Card>
        {cardHeader}

        {holdings.length === 0 ? (
          <EmptyState
            message={
              periods.length === 0
                ? 'Belum ada data portfolio holdings untuk fund ini.'
                : 'Tidak ada holdings yang tercatat untuk periode ini.'
            }
            compact
          />
        ) : (
          <>
            <div
              className="grid px-4 py-2.5 border-b"
              style={{
                gridTemplateColumns: '1fr auto',
                background: '#F8FAFC',
                borderColor: BRAND.panelBorder,
              }}
            >
              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#94A3B8' }}>
                Nama Efek
              </span>
              <span
                className="text-xs font-semibold uppercase tracking-wider text-right"
                style={{ color: '#94A3B8' }}
              >
                Sektor
              </span>
            </div>

            <ul className="list-none m-0 p-0">
              {holdings.map((holding, i) => (
                <li
                  key={holding.rank}
                  className="grid items-center px-4 py-3 gap-3"
                  style={{
                    gridTemplateColumns: '1fr auto',
                    background: i % 2 === 0 ? '#fff' : '#FAFBFD',
                    borderTop: i === 0 ? 'none' : '1px solid rgba(11,58,92,0.05)',
                  }}
                >
                  <p className="text-sm leading-snug min-w-0" style={{ color: BRAND.ink, fontWeight: 500 }}>
                    <span
                      className="text-[10px] font-bold mr-2 align-middle tabular-nums"
                      style={{ color: BRAND.textFaint }}
                    >
                      {String(holding.rank).padStart(2, '0')}
                    </span>
                    {holding.securityName}
                  </p>
                  <SectorChip sector={holding.sector} />
                </li>
              ))}
            </ul>

            <div
              className="px-4 py-3 border-t text-xs"
              style={{ borderColor: 'rgba(11,58,92,0.06)', color: '#94A3B8' }}
            >
              {holdings.length} instrumen · {fund ? sflShortName(fund.code) : EM_DASH} ·{' '}
              {period ? formatPeriodId(period) : EM_DASH}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
